import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogRef,
  MatDialogClose,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { DialogShellComponent } from 'src/app/core/ui/dialog-shell/dialog-shell.component';
import {
  DialogButtonStyle,
  DialogTone,
  cancelButtonStyle,
  confirmButtonStyle,
  confirmationTone,
} from 'src/app/core/services/dialog-shell-rules';
import {
  ConfirmationDialogData,
} from './confirmation-dialog.types';

export type {
  ConfirmationDialogBinaryChoice,
  ConfirmationDialogCheckbox,
  ConfirmationDialogCloseResult,
  ConfirmationDialogData,
} from './confirmation-dialog.types';
export {
  getConfirmationDialogCheckboxChecked,
  getConfirmationDialogHasImages,
  isConfirmationDialogCheckboxResult,
  isConfirmationDialogHasImagesResult,
} from './confirmation-dialog.utils';

/**
 * The one confirmation dialog of the app, opened through PopupAndNotifService (openConfirmation,
 * confirmAction, confirmDelete, confirm). Built on the shared dialog shell: Cancel is neutral, the
 * confirm button is pink only for a destructive action (tone `danger`, or confirmColor `warn`).
 * The message is bound as HTML through Angular's sanitizer: callers escape values that come from
 * the database or from user input (escapeHtml in core/utils/html.util.ts).
 */
@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDialogClose,
    DialogShellComponent,
  ],
  template: `
    <app-dialog-shell
      [title]="data.title"
      [tone]="tone"
      [icon]="data.icon ?? null"
      [closable]="!!data.dismissText"
      [closeLabel]="data.dismissText || 'Close'"
      [reverseActions]="!!data.reverseButtons"
      (closeClick)="dialogRef.close(null)">
      <div class="dialog-message-text" [innerHTML]="data.message"></div>
      <mat-checkbox
        *ngIf="data.checkbox"
        class="dialog-checkbox"
        [checked]="checkboxChecked"
        (change)="onCheckboxChange($event.checked)">
        {{ data.checkbox.label }}
      </mat-checkbox>
      <div *ngIf="data.binaryChoice" class="dialog-binary-choice-block">
        <p class="dialog-binary-question">{{ data.binaryChoice.question }}</p>
        <mat-button-toggle-group
          class="dialog-binary-choice"
          [value]="documentHasImages"
          (change)="onBinaryChoiceChange($event.value)"
          hideSingleSelectionIndicator>
          <mat-button-toggle [value]="true">
            {{ data.binaryChoice.yesText || 'Yes' }}
          </mat-button-toggle>
          <mat-button-toggle [value]="false">
            {{ data.binaryChoice.noText || 'No' }}
          </mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <ng-container dialogActions>
        <button
          mat-flat-button
          type="button"
          [class]="'ap-dialog-btn ap-dialog-btn--' + cancelStyle"
          [mat-dialog-close]="false">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button
          *ngIf="!data.checkbox && !data.binaryChoice"
          mat-flat-button
          type="button"
          [class]="'ap-dialog-btn ap-dialog-btn--' + confirmStyle"
          [mat-dialog-close]="true"
          cdkFocusInitial>
          {{ data.confirmText || 'Confirm' }}
        </button>
        <button
          *ngIf="data.checkbox || data.binaryChoice"
          mat-flat-button
          type="button"
          [class]="'ap-dialog-btn ap-dialog-btn--' + confirmStyle"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Confirm' }}
        </button>
      </ng-container>
    </app-dialog-shell>
  `,
  styles: [`
    .dialog-message-text {
      margin: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    .dialog-checkbox {
      display: block;
      margin-top: 14px;
      font-size: 0.88rem;
      line-height: 1.45;
    }
    .dialog-binary-choice-block {
      margin-top: 16px;
    }
    .dialog-binary-question {
      margin: 0 0 10px;
      font-size: 0.9rem;
      font-weight: 600;
      line-height: 1.45;
      color: var(--ap-color-text);
    }
    .dialog-binary-choice {
      display: flex;
      width: 100%;
      border-radius: var(--ap-radius-button-dialog);
      overflow: hidden;
      border: 1px solid var(--ap-color-border);
    }
    :host ::ng-deep .dialog-binary-choice .mat-button-toggle {
      flex: 1 1 0;
      font-weight: 600;
    }
    :host ::ng-deep .dialog-binary-choice .mat-button-toggle-checked {
      background: var(--ap-color-primary) !important;
      color: var(--ap-color-on-primary) !important;
    }
  `]
})
export class ConfirmationDialogComponent {
  checkboxChecked = false;
  documentHasImages = true;
  readonly tone: DialogTone;
  readonly confirmStyle: DialogButtonStyle;
  readonly cancelStyle: DialogButtonStyle;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationDialogData
  ) {
    this.checkboxChecked = !!data.checkbox?.defaultChecked;
    this.documentHasImages = data.binaryChoice?.defaultYes ?? true;
    this.tone = confirmationTone(data.tone, data.confirmColor);
    this.confirmStyle = confirmButtonStyle(this.tone);
    this.cancelStyle = cancelButtonStyle(this.tone);
  }

  onCheckboxChange(checked: boolean): void {
    this.checkboxChecked = checked;
  }

  onBinaryChoiceChange(value: boolean): void {
    this.documentHasImages = value;
  }

  onConfirm(): void {
    if (this.data.binaryChoice) {
      this.dialogRef.close({ confirmed: true, hasImages: this.documentHasImages });
      return;
    }
    this.dialogRef.close({ confirmed: true, checked: this.checkboxChecked });
  }
}
